import { useEffect, useState } from "react";
import { TrendingUp, Sparkles, Info, X } from "lucide-react";
import api from "@/lib/api";

export default function ScoreCard() {
  const [data, setData] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    api.get("/score").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;

  const radius = 60;
  const circumference = 2 * Math.PI * radius;
  const progressFraction = data.score / 1000;
  const dashOffset = circumference * (1 - progressFraction);

  return (
    <>
      <div
        onClick={() => setShowDetails(true)}
        className="bg-white border border-border rounded-2xl p-5 cursor-pointer hover:border-kudi-green/40 hover:shadow-sm transition relative overflow-hidden"
        data-testid="score-card"
      >
        <div className="absolute -right-6 -bottom-6 w-32 h-32 rounded-full opacity-10" style={{ backgroundColor: data.tier.color }} aria-hidden />
        <div className="relative flex items-center gap-5">
          {/* Ring */}
          <div className="relative w-[140px] h-[140px] shrink-0">
            <svg width="140" height="140" viewBox="0 0 140 140" className="transform -rotate-90">
              <circle cx="70" cy="70" r={radius} fill="none" stroke="#F5F5F0" strokeWidth="10" />
              <circle
                cx="70" cy="70" r={radius} fill="none"
                stroke={data.tier.color} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={circumference}
                strokeDashoffset={dashOffset}
                style={{ transition: "stroke-dashoffset 1s ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="text-3xl">{data.tier.emoji}</div>
              <div className="font-heading text-2xl font-bold tabular-nums" data-testid="score-value">{data.score}</div>
              <div className="text-[10px] text-kudi-ink/55 uppercase tracking-wider">of 1000</div>
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-kudi-ink/55 uppercase tracking-wider font-semibold">Kobolog Score</div>
            <div className="font-heading text-xl font-bold mt-1" style={{ color: data.tier.color }}>
              {data.tier.name}
            </div>
            <p className="text-xs text-kudi-ink/60 mt-1.5 leading-snug">
              {data.score >= 1000
                ? "You've maxed it out! 🚀"
                : `${data.next_threshold - data.score} pts to next tier`}
            </p>
            <button className="mt-2 text-xs font-semibold text-kudi-green flex items-center gap-1">
              <Info className="w-3 h-3" /> How is this calculated?
            </button>
          </div>
        </div>
      </div>

      {showDetails && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center" onClick={() => setShowDetails(false)}>
          <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-3xl p-6 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="score-details">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-xl font-bold">Your Kobolog Score</h2>
              <button onClick={() => setShowDetails(false)} className="w-8 h-8 rounded-full bg-accent flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>

            <div className="bg-gradient-to-br rounded-2xl p-5 text-white text-center" style={{ background: `linear-gradient(135deg, ${data.tier.color}, ${data.tier.color}dd)` }}>
              <div className="text-5xl">{data.tier.emoji}</div>
              <div className="font-heading text-4xl font-bold mt-1 tabular-nums">{data.score}</div>
              <div className="text-sm opacity-90">{data.tier.name} tier</div>
            </div>

            <div className="mt-5">
              <div className="text-xs font-semibold text-kudi-ink/70 uppercase tracking-wider mb-2">Score breakdown</div>
              <div className="space-y-2">
                <Row label="Starting base" value={data.breakdown.base} max={300} color="#94A3B8" />
                <Row label={`Activity (${data.stats.total_entries} entries)`} value={data.breakdown.activity} max={250} color="#10B981" />
                <Row label={`Consistency (${data.stats.days_with_activity} days)`} value={data.breakdown.consistency} max={200} color="#0EA5E9" />
                <Row label={`Debt recovery (${data.stats.debts_cleared})`} value={data.breakdown.debt_recovery} max={150} color="#F59E0B" />
                <Row label={`Loan repayment (${data.stats.loans_completed})`} value={data.breakdown.loan_repayment} max={100} color="#8B5CF6" />
              </div>
            </div>

            <div className="mt-5 bg-accent rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-kudi-green" />
                <div className="font-heading font-semibold text-sm">Grow your score</div>
              </div>
              <ul className="space-y-1.5 text-sm text-kudi-ink/75">
                {data.tips.map((t, i) => (
                  <li key={i} className="flex gap-2"><span className="text-kudi-green">•</span>{t}</li>
                ))}
              </ul>
            </div>

            <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
              <strong>Why this matters:</strong> Your score unlocks bigger loans, lower interest, and exclusive features. Banks may even use it for credit decisions.
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value, max, color }) {
  const pct = (value / max) * 100;
  return (
    <div>
      <div className="flex justify-between text-xs">
        <span className="text-kudi-ink/70">{label}</span>
        <span className="tabular-nums font-semibold">+{value} <span className="text-kudi-ink/40">/ {max}</span></span>
      </div>
      <div className="mt-1 bg-muted rounded-full h-1.5 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}
